import React, { useState, useEffect, useRef, useCallback } from "react";
import { parseErrorMessage } from "@/lib/parseErrorMessage";
import { Check, ChevronsUpDown, X, Loader2, MapPin, AlertTriangle, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { LocationSelectorProps, SelectedLocation, MetaLocation } from "@/types/geoTargeting";
import { CityRadiusSelector } from "./CityRadiusSelector";

const locationTypeEmoji: Record<string, string> = {
  country: "🌍",
  region: "🗺️",
  city: "🏙️",
  zip: "📮",
};

// Location groups for mutual exclusivity
type LocationGroup = "country" | "region" | "city_zip";

const getLocationGroup = (type: string): LocationGroup => {
  if (type === "country") return "country";
  if (type === "region") return "region";
  return "city_zip"; // city and zip are in the same group
};

const groupLabels: Record<LocationGroup, string> = {
  country: "countries",
  region: "regions",
  city_zip: "cities/zip codes",
};

export function LocationSelector({
  value,
  onChange,
  specialAdCategory,
  allowedCountries,
  disabled = false,
  placeholder = "Search locations...",
}: LocationSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<MetaLocation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingCityKey, setEditingCityKey] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { toast } = useToast();

  const isSpecialAdCategory = Boolean(specialAdCategory);

  // SAC: 15mi mandatory, Standard: 10mi optional (but we set default for convenience)
  const defaultRadius = isSpecialAdCategory ? 15 : 25;

  // Determine current location group from existing selections
  const getCurrentGroup = useCallback((): LocationGroup | null => {
    if (value.length === 0) return null;
    return getLocationGroup(value[0].type);
  }, [value]);

  // Check if user has cities or zips selected (for SAC city/zip mixing prevention)
  const hasCities = value.some((l) => l.type === "city");
  const hasZips = value.some((l) => l.type === "zip");
  const hasCityZipMixing = hasCities && hasZips;

  // For SAC campaigns, determine if a location type is blocked
  const isLocationTypeBlockedForSAC = useCallback(
    (locationType: string): { blocked: boolean; reason: string | null } => {
      if (!isSpecialAdCategory) return { blocked: false, reason: null };
      
      if (locationType === "city" && hasZips) {
        return {
          blocked: true,
          reason: "Special Ad Category campaigns cannot mix cities and ZIP codes. Remove existing ZIP codes to add cities.",
        };
      }
      if (locationType === "zip" && hasCities) {
        return {
          blocked: true,
          reason: "Special Ad Category campaigns cannot mix cities and ZIP codes. Remove existing cities to add ZIP codes.",
        };
      }
      return { blocked: false, reason: null };
    },
    [isSpecialAdCategory, hasCities, hasZips],
  );

  const searchLocations = useCallback(
    async (query: string) => {
      if (query.length < 2) {
        setSearchResults([]);
        setError(null);
        return;
      }

      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsLoading(true);
      setError(null);

      try {
        const { data, error: invokeError } = await supabase.functions.invoke("search-meta-locations", {
          body: {
            query,
            locationTypes: ["country", "region", "city", "zip"],
            allowedCountries,
          },
        });

        // If this request was aborted while waiting, bail out
        if (controller.signal.aborted) return;

        if (invokeError) {
          throw new Error(`Search failed: ${invokeError.message}`);
        }

        setSearchResults(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (err?.name === "AbortError") {
          return; // Request was cancelled, ignore
        }
        console.error("Location search error:", err);
        setError(parseErrorMessage(err));
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    },
    [allowedCountries],
  );

  // Debounced search
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      searchLocations(searchQuery);
    }, 300);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchQuery, searchLocations]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const isSelected = (location: MetaLocation) => {
    return value.some((l) => l.key === location.key);
  };

  const toggleLocation = (location: MetaLocation) => {
    if (isSelected(location)) {
      // Remove location
      onChange(value.filter((l) => l.key !== location.key));
    } else {
      // Check SAC city/zip mixing restriction
      const sacBlockStatus = isLocationTypeBlockedForSAC(location.type);
      if (sacBlockStatus.blocked) {
        toast({
          title: "Location type not allowed",
          description: sacBlockStatus.reason,
          variant: "destructive",
        });
        return;
      }

      // Add location - check for group conflict
      const newGroup = getLocationGroup(location.type);
      const currentGroup = getCurrentGroup();

      let newValue = [...value];

      // If adding a location from a different group, remove all existing locations
      if (currentGroup !== null && currentGroup !== newGroup) {
        const removedCount = newValue.length;
        newValue = [];

        toast({
          title: "Location group changed",
          description: `Removed ${removedCount} ${groupLabels[currentGroup]} because you selected a ${location.type}. Meta only allows targeting one location type group at a time.`,
          variant: "default",
        });
      }

      // Create the new location object
      const newLocation: SelectedLocation = {
        ...location,
        // Add radius for city and zip types
        ...((location.type === "city" || location.type === "zip") && {
          radius: defaultRadius,
          distance_unit: "mile" as const,
        }),
      };

      onChange([...newValue, newLocation]);
    }
  };

  const removeLocation = (key: string) => {
    onChange(value.filter((l) => l.key !== key));
    if (editingCityKey === key) {
      setEditingCityKey(null);
    }
  };

  const updateCityRadius = (updated: SelectedLocation) => {
    onChange(value.map((l) => (l.key === updated.key ? updated : l)));
    setEditingCityKey(null);
  };

  const formatLocationLabel = (location: SelectedLocation) => {
    const emoji = locationTypeEmoji[location.type] || "📍";
    let label = `${emoji} ${location.name}`;

    if ((location.type === "city" || location.type === "zip") && location.radius) {
      const unit = location.distance_unit === "kilometer" ? "km" : "mi";
      label += ` (${location.radius} ${unit})`;
    }

    return label;
  };

  // Get info text about current targeting mode
  const getTargetingModeInfo = () => {
    const currentGroup = getCurrentGroup();
    if (!currentGroup) return null;

    const typeLabels: Record<LocationGroup, string> = {
      country: "country",
      region: "regions",
      city_zip: hasCities && hasZips ? "cities/ZIP codes" : hasCities ? "cities" : "ZIP codes",
    };

    const label = typeLabels[currentGroup];
    const selectionWord = currentGroup === "country" ? "selection" : "selections";
    return `Currently targeting by ${label}. Adding a different location type will replace existing ${selectionWord}.`;
  };

  return (
    <div className="space-y-3">
      {/* SAC-specific warning banner */}
      {isSpecialAdCategory && (
        <Alert className="border-amber-500/50 bg-amber-500/10">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <AlertDescription className="text-amber-600 text-sm space-y-1">
            <p>
              <strong>Special Ad Category ({specialAdCategory}):</strong> Location targeting restricted to{" "}
              {allowedCountries && allowedCountries.length > 0 ? allowedCountries.join(", ") : "allowed countries"} only.
              Cities and ZIP codes require a 15-50 mile radius (mandatory).
            </p>
            {hasCityZipMixing && (
              <p className="text-red-600 font-medium">
                ⚠️ You cannot mix cities and ZIP codes for Special Ad Category campaigns.
              </p>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Current targeting mode info - for ALL campaigns */}
      {value.length > 0 && (
        <Alert className="border-blue-500/30 bg-blue-500/5">
          <Info className="h-4 w-4 text-blue-500" />
          <AlertDescription className="text-blue-600 text-sm">{getTargetingModeInfo()}</AlertDescription>
        </Alert>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between"
          >
            <span className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4" />
              {placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search countries, regions, cities, or zip codes..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {searchQuery.length < 2 && (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  Type at least 2 characters to search
                </div>
              )}

              {searchQuery.length >= 2 && isLoading && (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}

              {searchQuery.length >= 2 && error && (
                <div className="p-4 text-center text-sm text-destructive">{error}</div>
              )}

              {searchQuery.length >= 2 && !isLoading && !error && searchResults.length === 0 && (
                <CommandEmpty>No locations found</CommandEmpty>
              )}

              {searchQuery.length >= 2 && !isLoading && !error && searchResults.length > 0 && (
                <CommandGroup>
                  {searchResults.map((location) => {
                    const selected = isSelected(location);
                    const emoji = locationTypeEmoji[location.type] || "📍";
                    const locationGroup = getLocationGroup(location.type);
                    const currentGroup = getCurrentGroup();
                    const willReplaceExisting = currentGroup !== null && currentGroup !== locationGroup && !selected;
                    
                    // Check if this location type is blocked for SAC
                    const sacBlockStatus = isLocationTypeBlockedForSAC(location.type);
                    const isBlocked = sacBlockStatus.blocked && !selected;

                    return (
                      <CommandItem
                        key={location.key}
                        value={location.key}
                        onSelect={() => toggleLocation(location)}
                        className={cn(
                          "cursor-pointer",
                          willReplaceExisting && !isBlocked && "border-l-2 border-amber-500",
                          isBlocked && "opacity-50 cursor-not-allowed border-l-2 border-red-500",
                        )}
                        disabled={isBlocked}
                      >
                        <Check className={cn("mr-2 h-4 w-4", selected ? "opacity-100" : "opacity-0")} />
                        <span className="mr-2">{emoji}</span>
                        <div className="flex flex-col flex-1">
                          <span className={cn(isBlocked && "text-muted-foreground")}>{location.name}</span>
                          {location.country_name && location.type !== "country" && (
                            <span className="text-xs text-muted-foreground">
                              {location.region && `${location.region}, `}
                              {location.country_name}
                            </span>
                          )}
                          {isBlocked && (
                            <span className="text-xs text-red-500">
                              Cannot mix with existing {hasCities ? "cities" : "ZIP codes"}
                            </span>
                          )}
                        </div>
                        {willReplaceExisting && !isBlocked && (
                          <span className="text-xs text-amber-500 ml-2">replaces {groupLabels[currentGroup!]}</span>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected locations as chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((location) => (
            <div key={location.key} className="relative">
              <Badge
                variant="secondary"
                className={cn(
                  "pr-7 cursor-default",
                  (location.type === "city" || location.type === "zip") && "cursor-pointer hover:bg-secondary/80",
                )}
                onClick={() => {
                  if (location.type === "city" || location.type === "zip") {
                    setEditingCityKey(editingCityKey === location.key ? null : location.key);
                  }
                }}
              >
                {formatLocationLabel(location)}
              </Badge>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeLocation(location.key);
                }}
                className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-muted-foreground/20"
              >
                <X className="h-3 w-3" />
              </button>

              {/* City/Zip radius selector popover */}
              {(location.type === "city" || location.type === "zip") && editingCityKey === location.key && (
                <CityRadiusSelector
                  location={location}
                  onChange={updateCityRadius}
                  onClose={() => setEditingCityKey(null)}
                  isSpecialAdCategory={isSpecialAdCategory}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
